import {
  CreateSupplierRequestsRequest,
  CreateRequestForQuotationRequest,
  ProjectSupplierDto,
  QuotationExchangeChannel,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { GetContext } from 'store/storeUtils'
import { OutgoingRfqsController } from '../RFQ/OutgoingRfqsController'
import { RFQLine } from './Components/ProjectRFQLines'

export type RfqRequestDetails = Omit<
  CreateRequestForQuotationRequest,
  'buyerProjectId' | 'bomItemAndActivities'
>

export type SupplierAllocation = {
  supplier: ProjectSupplierDto
  activityKeys: string[]
  channel: QuotationExchangeChannel
  senderChannelId?: string
}

export const activityKey = (bomItemId?: string, activityId?: string) =>
  `${bomItemId ?? ''}:${activityId ?? ''}`

export const preferredChannel = (supplier: ProjectSupplierDto) => {
  const channels =
    supplier.configuredEndpoints?.map((item) => item.channel) ?? []
  if (channels.includes(QuotationExchangeChannel.Scsn)) {
    return QuotationExchangeChannel.Scsn
  }
  if (channels.includes(QuotationExchangeChannel.Email)) {
    return QuotationExchangeChannel.Email
  }
  return QuotationExchangeChannel.Manual
}

export class ProjectRFQController extends OutgoingRfqsController {
  private _projectId: string
  constructor() {
    super()
    this._projectId = GetContext().projectId
  }

  public async getProjectRFQs() {
    return this.listCanonical(this._projectId)
  }

  public async createRequestForQuotation(
    draftRFQLines: RFQLine[],
    details: RfqRequestDetails = {},
  ) {
    try {
      const request: CreateRequestForQuotationRequest = {
        buyerProjectId: this._projectId,
        bomItemAndActivities: draftRFQLines.map((line) => ({
          bomItemId: line.bomItemPointer.id,
          activities: (line.activities ?? [])
            .filter((activity) => activity.selected)
            .map((activity) => ({
              buyerActivityId: activity.activityId,
              workingStepType: activity.workingStepType,
              requestedQuantityBreaks: activity.quantityBreaks,
            })),
        })),
        ...details,
      }

      return await this.createCanonical(request)
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async publishRequestForQuotation(
    rfqId: string,
    allocations: SupplierAllocation[],
  ) {
    const supplierRequests: NonNullable<
      CreateSupplierRequestsRequest['supplierRequests']
    > = allocations.map((allocation) => {
      const request = {
        supplierId: allocation.supplier.supplierId,
        channel: allocation.channel,
        activities: allocation.activityKeys.map((key) => {
          const [buyerBoMItemId, buyerActivityId] = key.split(':')
          return { buyerBoMItemId, buyerActivityId }
        }),
      }
      return allocation.channel === QuotationExchangeChannel.Manual
        ? {
            ...request,
            sender: { channel: QuotationExchangeChannel.Manual },
          }
        : request
    })
    const invitations = await this.createSupplierRequests(rfqId, {
      supplierRequests,
    })
    const results = await Promise.allSettled(
      invitations.map((invitation) => this.sendDelivery(invitation.id!)),
    )
    return invitations.map((invitation, index) => ({
      invitation,
      result: results[index],
    }))
  }
}
